import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowRight, Sparkles, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const go = (profile) => {
    if (profile && profile.onboarded) navigate("/app");
    else navigate("/onboarding");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
      } else {
        const profile = mode === "login" ? await login(email, password) : await register(email, password, name);
        toast.success(mode === "login" ? "Welcome back" : "Account created");
        go(profile);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const profile = await login("demo@leadforge.ai", "demo1234");
      toast.success("Signed in to the live demo");
      go(profile);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-slate-800 relative overflow-hidden lf-radial">
        <div className="absolute inset-0 lf-grid opacity-30" />
        <div className="relative"><Brand /></div>
        <div className="relative max-w-md">
          <h2 className="font-display text-3xl font-bold text-slate-50 leading-tight">Stop searching for leads. Start finding opportunities.</h2>
          <p className="text-slate-400 mt-4 text-sm leading-relaxed">Sign in and click <span className="text-primary font-medium">Find My Best Clients</span> to see your top 10 opportunities — scored, explained, and ready to reach out.</p>
        </div>
        <div className="relative text-xs text-slate-600">© 2026 LeadForge AI</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Brand /></div>
          <h1 className="font-display text-2xl font-bold text-slate-50">{mode === "login" ? "Sign in" : mode === "register" ? "Create your account" : "Reset your password"}</h1>
          <p className="text-sm text-slate-400 mt-1 mb-6">{mode === "login" ? "Welcome back to LeadForge AI." : mode === "register" ? "Start finding your best clients in minutes." : "Enter your email and we'll send you a reset link."}</p>

          {mode !== "forgot" && (
            <>
              <Button onClick={demoLogin} disabled={loading} data-testid="demo-login-btn" className="w-full h-11 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 mb-5">
                <Sparkles className="w-4 h-4 mr-2" /> Try the live demo instantly
              </Button>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-slate-800" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-600">or</span>
                <div className="h-px flex-1 bg-slate-800" />
              </div>
            </>
          )}

          {mode === "forgot" && sent ? (
            <div className="rounded-xl border border-primary/25 bg-primary/5 p-5 text-center" data-testid="forgot-sent">
              <MailCheck className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-sm text-slate-200 font-medium">Check your email</p>
              <p className="text-xs text-slate-400 mt-1">If an account exists for {email}, a password reset link is on its way. The link expires in 1 hour.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label className="text-slate-300 text-xs">Agency name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Digital" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="auth-name-input" />
                </div>
              )}
              <div>
                <Label className="text-slate-300 text-xs">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@agency.com" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="auth-email-input" />
              </div>
              {mode !== "forgot" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-xs">Password</Label>
                    {mode === "login" && (
                      <button type="button" className="text-[11px] text-primary hover:underline" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setSent(false); }}>Forgot password?</button>
                    )}
                  </div>
                  <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="auth-password-input" />
                </div>
              )}
              {error && <p className="text-xs text-red-400" data-testid="auth-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-emerald-600 text-white" data-testid="auth-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>{mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"} <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
          )}

          <p className="text-sm text-slate-400 mt-6 text-center">
            {mode === "forgot" ? (
              <button className="text-primary hover:underline font-medium" data-testid="auth-back-login" onClick={() => { setMode("login"); setError(""); setSent(false); }}>Back to sign in</button>
            ) : (
              <>
                {mode === "login" ? "New to LeadForge?" : "Already have an account?"}{" "}
                <button className="text-primary hover:underline font-medium" data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
